import moment, { Moment } from 'moment';
import { clientDb } from '../firebase';
import { vals, makeAudit } from '../helpers';
import { iList, iNote } from '../interfaces';
import { ItemType, UserAuth } from '../interfaces';

interface RawNote {
  id: string;
  date: Date | number | string;
  note: string;
  file?: iNote['file'] | null;
  po?: string | null;
  techName?: string;
  completedDate?: string;
  amount?: number | null;
  complete?: boolean;
  extraValues?: iNote['extraValues'];
}

type NotesByItem = Record<string, Record<string, RawNote>>;

interface NotesData {
  device?: NotesByItem;
  fence?: NotesByItem;
}

const convertNote = (raw: any) => ({
  id: raw.id,
  date: moment(raw.date),
  note: raw.note,
  file: raw.file || null,
  po: raw.po || null,
  techName: raw.techName || '',
  completedDate: raw.completedDate || '',
  amount: raw.amount ?? null,
  complete: raw.complete ?? false,
  extraValues: raw.extraValues || {},
});

export const updateNote = (user: UserAuth) => async (
  itemId: string,
  noteId: string,
  type: 'device' | 'fence',
  noteData: {
    note: string;
    file?: iNote['file'][];
    po?: string;
    techName?: string;
    date: Moment,
    updatedAt: Moment,
    completedDate?: string;
    amount?: number | null;
    complete?: boolean;
    part?: string;
    service?: string;
    vendor?: string;
    repairShop?: string;
    extraValues?: iNote['extraValues'];
  },
) => {
  await clientDb().update(
    makeAudit(user, {
      [`notes/${type}/${itemId}/${noteId}`]: {
        updatedAt: new Date(),
        date: noteData.date.toDate(),
        note: noteData.note,
        id: noteId,
        file: noteData.file || null,
        po: noteData.po || '',
        techName: noteData.techName || '',
        completedDate: noteData.completedDate || '',
        amount: noteData.amount ?? null,
        complete: noteData.complete ?? false,
        part: noteData.part || '',
        service: noteData.service || '',
        vendor: noteData.vendor || '',
        repairShop: noteData.repairShop || '',
        extraValues: noteData.extraValues || {},
      },
    }),
  );
};

export const deleteNote =
  (user: UserAuth) =>
  async (itemId: string, noteId: string, type: 'device' | 'fence') => {
    await clientDb().update(
      makeAudit(user, { [`notes/${type}/${itemId}/${noteId}`]: null }),
    );
  };

export const getNote = async (
  itemId: string,
  type: 'device' | 'fence',
  noteId: string,
) =>
  (await clientDb()
    .child('notes')
    .child(type.toString())
    .child(itemId)
    .child(noteId)
    .once('value')).val() as iNote | null;

export const getNotes = async (itemId: string, type: ItemType) => {
  const raw = (await clientDb()
    .child('notes')
    .child(type.toString())
    .child(itemId)
    .once('value')).val();

  if (!raw) return {} as iList<iNote>;

  const ret: any = {};

  vals(raw).map(convertNote).forEach((note) => (ret[note.id] = note));

  return ret as iList<iNote>;
};

const ORDER_PATH = 'notes/order';

export const getFieldOrder = async (): Promise<string[] | null> => {
  const snapshot = await clientDb().child(ORDER_PATH).once('value');
  const val = snapshot.val();
  return Array.isArray(val) ? val : null;
};

export const saveFieldOrder =
  (user: UserAuth) =>
  async (newOrder: string[]): Promise<void> => {
    await clientDb().update(
      makeAudit(user, {
        [ORDER_PATH]: newOrder,
      }),
    );
  };

export const getAllTechNames = async (): Promise<string[]> => {
  try {
    const notesRef = clientDb().child('notes');
    const snapshot = await notesRef.once('value');
    const notesData = snapshot.val() as NotesData | null;

    if (!notesData) return [];

    const techNamesSet = new Set<string>();

    const processNotes = (notes: NotesByItem | undefined): void => {
      if (!notes) return;

      Object.values(notes).forEach((itemNotes) => {
        if (!itemNotes) return;

        Object.values(itemNotes).forEach((note) => {
          if (note?.techName && note.techName.trim()) {
            techNamesSet.add(note.techName.trim());
          }
        });
      });
    };

    processNotes(notesData.device);
    processNotes(notesData.fence);

    return Array.from(techNamesSet).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }),
    );
  } catch (error) {
    console.error('Error fetching tech names:', error);
    return [];
  }
};

const GLOBAL_VISIBLE_FIELDS_PATH = 'notes/globalVisibleFields';

export const getGlobalVisibleFields = async (): Promise<string[] | null> => {
  const snapshot = await clientDb().child(GLOBAL_VISIBLE_FIELDS_PATH).once('value');
  const val = snapshot.val();
  return Array.isArray(val) ? val : null;
};

export const saveGlobalVisibleFields =
  (user: UserAuth) =>
  async (fields: string[]): Promise<void> => {
    await clientDb().update(
      makeAudit(user, {
        [GLOBAL_VISIBLE_FIELDS_PATH]: fields,
      }),
    );
  };

export const getDeviceTotalSpent = async (deviceId: string): Promise<number> => {
  const ref = clientDb().child(`notes/device/${deviceId}`);
  const snapshot = await ref.once('value');
  const notesById = snapshot.val() as Record<string, iNote> | null;

  if (!notesById) return 0;

  return Object.values(notesById).reduce((sum, n) => {
    const a = n?.amount;
    return sum + (typeof a === 'number' && Number.isFinite(a) ? a : 0);
  }, 0);
};

export const getAllSelectOptions = async (): Promise<Record<string, string[]>> => {
  try {
    const notesRef = clientDb().child('notes');
    const snapshot = await notesRef.once('value');
    const notesData = snapshot.val() as NotesData | null;

    if (!notesData) return {};

    const optionsByField = new Map<string, Set<string>>();

    const addOptions = (fieldId: string, values: string[]) => {
      if (!optionsByField.has(fieldId)) optionsByField.set(fieldId, new Set());
      const set = optionsByField.get(fieldId)!;
      values.forEach((v) => {
        const trimmedValue = String(v).trim();
        if (trimmedValue) set.add(trimmedValue);
      });
    };

    const processNotes = (notes: NotesByItem | undefined): void => {
      if (!notes) return;

      Object.values(notes).forEach((itemNotes) => {
        if (!itemNotes) return;

        Object.values(itemNotes).forEach((note) => {
          const ev = note?.extraValues as Record<string, { options?: unknown[]; value?: unknown }> | undefined;
          if (!ev || typeof ev !== 'object') return;

          Object.entries(ev).forEach(([fid, entry]) => {
            if (!entry) return;
            const opts = entry.options;
            if (Array.isArray(opts)) {
              addOptions(fid, opts.map((o) => String(o)));
            }
            const v = entry.value;
            if (v != null && typeof v === 'string' && v.trim()) {
              addOptions(fid, [v.trim()]);
            }
          });
        });
      });
    };

    processNotes(notesData.device);
    processNotes(notesData.fence);

    const result: Record<string, string[]> = {};
    optionsByField.forEach((set, fid) => {
      result[fid] = Array.from(set).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }),
      );
    });
    return result;
  } catch (error) {
    console.error('Error fetching select options:', error);
    return {};
  }
};

const SUGGESTION_PREFS_PATH = 'notes/suggestion_prefs';

export const encodeSuggestionKey = (value: string): string => {
  try {
    const b64 = btoa(unescape(encodeURIComponent(value)));
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  } catch (e) {
    try {
      const b64 = btoa(value);
      return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    } catch {
      return value.replace(/[.#$/[\]]/g, '_');
    }
  }
};

export const getSuggestionPrefs = async (): Promise<{
  hidden: Record<string, Record<string, boolean>>;
  rename: Record<string, Record<string, string>>;
}> => {
  try {
    const ref = clientDb().child(SUGGESTION_PREFS_PATH);
    const snap = await ref.once('value');
    const val = snap.val() || {};
    return {
      hidden: val.hidden || {},
      rename: val.rename || {},
    };
  } catch (e) {
    console.error(e);
    return { hidden: {}, rename: {} };
  }
};

export const hideSuggestionOption = (user: UserAuth) => async (fieldId: string, value: string) => {
  const prefKey = encodeSuggestionKey(value);

  await clientDb().update(
    makeAudit(user, {
      [`${SUGGESTION_PREFS_PATH}/hidden/${fieldId}/${prefKey}`]: true,
    }),
  );
};

export const renameSuggestionOption = (user: UserAuth) => async (fieldId: string, value: string, newLabel: string) => {
  const prefKey = encodeSuggestionKey(value);

  await clientDb().update(
    makeAudit(user, {
      [`${SUGGESTION_PREFS_PATH}/rename/${fieldId}/${prefKey}`]: String(newLabel || '').trim(),
    }),
  );
};

export const getHiddenOptions = async (): Promise<Record<string, Record<string, boolean>>> => {
  try {
    const ref = clientDb().child(`${SUGGESTION_PREFS_PATH}/hidden`);
    const snap = await ref.once('value');
    return snap.val() || {};
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const hideSelectOption = (user: UserAuth) => async (fieldId: string, value: string) => {
  await hideSuggestionOption(user)(fieldId, value);
};


export const getNotesForMultipleItems = async (
  type: 'device' | 'fence',
  ids: string[]
): Promise<iNote[]> => {
  if (!ids.length) return [];

  const safeIds = ids.map(String);

  const promises = safeIds.map(id =>
    clientDb().child(`notes/${type}/${id}`).once('value')
  );

  const snapshots = await Promise.all(promises);
  let allNotes: iNote[] = [];

  snapshots.forEach((snap, index) => {
    const data = snap.val();
    const parentId = safeIds[index];

    if (data) {
      const deviceNotes = Object.values(data).map((note: any) => ({
        ...note,
        parentId: parentId
      }));
      allNotes = [...allNotes, ...deviceNotes];
    }
  });

  return allNotes;
};

export const getAllNotesByCategory = async (type: 'device' | 'fence'): Promise<iNote[]> => {
  const ref = clientDb().child(`notes/${type}`);
  const snapshot = await ref.once('value');
  const data = snapshot.val();

  if (!data) return [];

  let allNotes: iNote[] = [];

  Object.entries(data).forEach(([deviceId, deviceNotes]: [string, any]) => {
    if (deviceNotes) {
       const notesWithParent = Object.values(deviceNotes).map((note: any) => ({
        ...note,
        parentId: deviceId
      }));
      allNotes = [...allNotes, ...notesWithParent];
    }
  });

  return allNotes;
};